<!--
  ApplicantStatistics.vue
  応募者統計ダッシュボード - グラフ統合型レイアウト
  Report.vue機能拡張 - デザインパターン3
  
  Created: 2025-08-11
  Author: Claude
-->
<template>
  <div class="applicant-statistics" id="export-content">
    <!-- ダッシュボードヘッダー -->
    <div class="stats-header">
      <div class="header-left">
        <h1 class="page-title">
          <i class="fas fa-chart-line mr-2"></i>応募者統計ダッシュボード
        </h1>
        <p class="page-subtitle">採用活動の主要指標をリアルタイムで監視</p>
      </div>
      <div class="header-controls">
        <div class="action-buttons">
          <button class="action-btn" @click="refreshData" :disabled="isLoading">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
            更新
          </button>
          <button class="action-btn" @click="exportData">
            <i class="fas fa-download"></i>
            出力
          </button>
          <button class="action-btn primary" @click="showCustomizeModal = true">
            <i class="fas fa-cog"></i>
            カスタマイズ
          </button>
        </div>
      </div>
    </div>

    <!-- KPI指標グリッド -->
    <div class="kpi-section">
      <draggable 
        v-model="allKpis"
        class="kpi-grid"
        :style="{ gridTemplateColumns: gridColumns }"
        :options="dragOptions"
        @change="onDragChange"
        :move="checkMove"
      >
        <KpiCard
          v-for="kpi in visibleKpis"
          :key="kpi.id"
          :kpi="kpi"
          :data="kpiData[kpi.id]"
          :is-loading="isLoading"
          @click="onKpiClick"
          class="draggable-item"
        />
      </draggable>
    </div>

    <!-- メインチャートセクション -->
    <div class="chart-section">
      <div class="chart-container">
        <div class="chart-header">
          <h2 class="chart-title">
            <i class="fas fa-chart-area mr-2"></i>
            詳細分析
          </h2>
          <div class="chart-tabs">
            <button
              v-for="tab in chartTabs"
              :key="tab.value"
              @click="activeChartTab = tab.value"
              :class="['chart-tab', { active: activeChartTab === tab.value }]"
            >
              <i :class="`fas fa-${tab.icon} mr-1`"></i>
              {{ tab.label }}
            </button>
          </div>
        </div>
        <div class="main-chart">
          <MainChart
            :type="activeChartTab"
            :data="chartData"
            :is-loading="isLoading"
          />
        </div>
      </div>

    </div>

    <!-- カスタマイズモーダル -->
    <CustomizeModal
      v-if="showCustomizeModal"
      :available-kpis="allKpis"
      :visible-kpis="visibleKpiIds"
      :current-layout-mode="layoutMode"
      @update="updateKpiSelection"
      @close="showCustomizeModal = false"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import draggable from 'vuedraggable'
import KpiCard from './components/KpiCard.vue'
import MainChart from './components/MainChart.vue'
import CustomizeModal from './components/CustomizeModal.vue'

// DashboardMetrics用の定数定義

// 採用関連のステージ名（採用転換率計算用）
// 実際の採用ステージ名に応じて調整が必要
const HIRED_STAGES = [
  '採用',
  '入社済み',
  '入社',
  '内定承諾',
  '採用決定',
  '採用予定'
]

// 面接関連のステージ名（参考情報）
// 実際の面接数はInterviewHistoryから取得
const INTERVIEW_STAGES = [
  '面接',
  '一次面接',
  '二次面接',
  '最終面接',
  '面接実施',
  '面接済み'
]

export default {
  name: 'ApplicantStatistics',
  components: {
    KpiCard,
    MainChart,
    CustomizeModal,
    draggable
  },
  props: {
    user: {
      type: Object,
      required: true,
      validator: (value) => {
        return value && (
          value.companyID || 
          (value.company && value.company.company_hash)
        );
      }
    }
  },
  data() {
    return {
      isLoading: false,
      activeChartTab: 'applications',
      showCustomizeModal: false,
      layoutMode: 'auto',
      
      // ドラッグ設定
      dragOptions: {
        animation: 150,
        group: 'kpi-cards',
        disabled: false,
        ghostClass: 'ghost-card',
        dragClass: 'dragging-card',
        chosenClass: 'chosen-card',
        forceFallback: true,
        fallbackOnBody: true,
        fallbackTolerance: 0,
        delay: 0,
        delayOnTouchOnly: false,
        touchStartThreshold: 0,
        swapThreshold: 0.65
      },
      
      // チャートタブオプション
      chartTabs: [
        { value: 'applications', label: '応募推移', icon: 'chart-line' },
        { value: 'interviews', label: '面談状況', icon: 'users' }
      ],
      
      // キャッシュ関連
      dataCache: {},
      cacheConfig: {
        weeklyInterviews: 5 * 60 * 1000,    // 5分
        conversionRate: 10 * 60 * 1000,     // 10分
        totalApplications: 30 * 60 * 1000   // 30分
      },
      
      // 遅延読み込み状態
      interviewDataLoaded: false,
      
      // すべてのKPI項目定義
      allKpis: [
        // 1番目：サマリー
        {
          id: 'summaryOverview',
          title: 'サマリー',
          icon: 'info-circle',
          color: '#34495e',
          unit: '',
          chartType: null,
          enabled: true,
          apiMetric: null,
          displayType: 'summary'
        },
        // 2番目：本日のタスク
        {
          id: 'todayTasks',
          title: '本日のタスク',
          icon: 'tasks',
          color: '#e74c3c',
          unit: '件',
          chartType: null,
          enabled: true,
          apiMetric: 'today_tasks',
          displayType: 'tasks'
        },
        {
          id: 'todayApplications',
          title: '本日の応募',
          icon: 'calendar-day',
          color: '#2ecc71',
          unit: '件',
          chartType: 'line',
          enabled: true,
          apiMetric: 'today_applications'
        },
        {
          id: 'weeklyApplications',
          title: '週間応募数',
          icon: 'calendar-week',
          color: '#9b59b6',
          unit: '件',
          chartType: 'bar',
          enabled: true,
          apiMetric: 'weekly_applications'
        },
        {
          id: 'monthlyApplications',
          title: '当月応募数',
          icon: 'calendar-alt',
          color: '#f39c12',
          unit: '件',
          chartType: 'line',
          enabled: true,
          apiMetric: 'current_month_applications'
        },
        {
          id: 'lastMonthApplications',
          title: '先月応募数',
          icon: 'history',
          color: '#e74c3c',
          unit: '件',
          chartType: 'line',
          enabled: true,
          apiMetric: 'last_month_applications'
        },
        {
          id: 'todayInterviews',
          title: '本日面接',
          icon: 'user-tie',
          color: '#1abc9c',
          unit: '件',
          chartType: 'bar',
          enabled: true,
          apiMetric: 'today_interviews'
        },
        {
          id: 'weeklyInterviews',
          title: '週間面接数',
          icon: 'calendar-week',
          color: '#16a085',
          unit: '件',
          chartType: 'bar',
          enabled: true,
          apiMetric: 'weekly_interviews'
        },
        {
          id: 'currentMonthInterviews',
          title: '当月面接数',
          icon: 'calendar-check',
          color: '#2ecc71',
          unit: '件',
          chartType: 'line',
          enabled: true,
          apiMetric: 'current_month_interviews'
        },
        {
          id: 'lastMonthInterviews',
          title: '先月面接数',
          icon: 'calendar-minus',
          color: '#27ae60',
          unit: '件',
          chartType: 'line',
          enabled: true,
          apiMetric: 'last_month_interviews'
        },
        {
          id: 'totalApplications',
          title: '総応募数',
          icon: 'infinity',
          color: '#3498db',
          unit: '件',
          chartType: 'line',
          enabled: true,
          apiMetric: null // 別途計算
        },
        // 追加可能な項目
        {
          id: 'conversionRate',
          title: '採用転換率',
          icon: 'percentage',
          color: '#e67e22',
          unit: '%',
          chartType: 'line',
          enabled: true,
          apiMetric: null
        },
        // 採用数カード
        {
          id: 'currentMonthHired',
          title: '当月採用数',
          icon: 'user-check',
          color: '#27ae60',
          unit: '名',
          chartType: 'line',
          enabled: true,
          apiMetric: 'current_month_hired'
        },
        // 採用数推移カード
        {
          id: 'hiredTrend',
          title: '採用数推移',
          icon: 'chart-line',
          color: '#2ecc71',
          unit: '名',
          chartType: 'line',
          enabled: true,
          apiMetric: 'hired_trend'
        },
        // 年度別カード
        {
          id: 'currentYearApplications',
          title: '今年度応募数',
          icon: 'calendar-alt',
          color: '#3498db',
          unit: '件',
          chartType: 'bar',
          enabled: true,
          apiMetric: 'current_year_applications'
        },
        {
          id: 'previousYearApplications',
          title: '前年度応募数',
          icon: 'calendar',
          color: '#2c3e50',
          unit: '件',
          chartType: 'bar',
          enabled: true,
          apiMetric: 'previous_year_applications'
        },
        {
          id: 'currentYearInterviews',
          title: '今年度面接数',
          icon: 'user-friends',
          color: '#1abc9c',
          unit: '件',
          chartType: 'line',
          enabled: true,
          apiMetric: 'current_year_interviews'
        },
        {
          id: 'previousYearInterviews',
          title: '前年度面接数',
          icon: 'users',
          color: '#16a085',
          unit: '件',
          chartType: 'line',
          enabled: true,
          apiMetric: 'previous_year_interviews'
        },
        {
          id: 'currentYearHired',
          title: '今年度採用数',
          icon: 'user-plus',
          color: '#2ecc71',
          unit: '名',
          chartType: 'bar',
          enabled: true,
          apiMetric: 'current_year_hired'
        },
        {
          id: 'previousYearHired',
          title: '前年度採用数',
          icon: 'user-check',
          color: '#27ae60',
          unit: '名',
          chartType: 'bar',
          enabled: true,
          apiMetric: 'previous_year_hired'
        }
      ],
      
      // KPIデータ
      kpiData: {},
      chartData: {},
      
      // サマリーデータ
      totalApplications: 0,
      totalInterviews: 0,
      conversionRate: 0,
      totalHired: 0,
      hiredThisMonth: 0,
      
      // ユーザーデータ取得のリトライカウンター
      userCheckRetries: 0,
      maxRetries: 10
    }
  },
  
  computed: {
    
    // 表示中のKPI項目のみフィルター
    visibleKpis() {
      return this.allKpis.filter(kpi => kpi.enabled);
    },
    
    visibleKpiIds() {
      return this.visibleKpis.map(kpi => kpi.id);
    },
    
    // グリッドレイアウトの列数を動的に調整
    gridColumns() {
      // レイアウトモードに応じて列数を決定
      if (this.layoutMode === 'grid3') {
        return 'repeat(3, 1fr)';
      } else if (this.layoutMode === 'grid4') {
        return 'repeat(4, 1fr)';
      }
      
      // 自動レイアウトの場合
      const count = this.visibleKpis.length;
      if (count <= 4) return `repeat(${count}, 1fr)`;
      if (count <= 6) return 'repeat(6, 1fr)';
      if (count <= 8) return 'repeat(4, 1fr)';
      if (count <= 12) return 'repeat(4, 1fr)';
      return 'repeat(5, 1fr)';
    }
  },
  
  mounted() {
    this.loadUserPreferences();
    this.refreshData();
    this.startRealTimeUpdates();
  },
  
  beforeUnmount() {
    this.stopRealTimeUpdates();
    // キャッシュのクリア
    this.dataCache = {};
    
    // イベントリスナーの削除
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  },
  
  watch: {
    activeChartTab: {
      handler(newTab, oldTab) {
        console.log(`Chart tab changed: ${oldTab} -> ${newTab}`);
        
        // 面談状況タブがアクティブになった時のみデータを読み込み
        if (newTab === 'interviews' && !this.interviewDataLoaded) {
          console.log('Loading interview data for the first time');
          this.fetchInterviewData();
          this.interviewDataLoaded = true;
        }
        
        
        // ユーザー設定を保存
        this.saveUserPreferences();
      },
      immediate: false
    }
  },
  
  methods: {
    ...mapActions('directtalk', [
      'fetchDashboardMetrics',
      'aggregateSeekers',
      'fetchSeekerSummary'
    ]),
    
    /**
     * キャッシュからデータを取得
     */
    getFromCache(key) {
      const cached = this.dataCache[key];
      if (!cached) return null;
      
      const now = Date.now();
      const cacheExpiry = cached.timestamp + (this.cacheConfig[key] || 300000); // デフォルト5分
      
      if (now > cacheExpiry) {
        delete this.dataCache[key];
        return null;
      }
      
      console.log(`Cache hit for key: ${key}`);
      return cached.data;
    },

    /**
     * キャッシュにデータを保存
     */
    setCache(key, data) {
      this.dataCache[key] = {
        data: data,
        timestamp: Date.now()
      };
      console.log(`Cache set for key: ${key}`);
    },

    /**
     * APIレスポンスをキャッシュして取得
     */
    async fetchWithCache(apiCall, cacheKey) {
      // キャッシュから取得を試行
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }
      
      // キャッシュにない場合はAPIを呼び出し
      try {
        const result = await apiCall();
        this.setCache(cacheKey, result);
        return result;
      } catch (error) {
        console.error(`API call failed for cache key: ${cacheKey}`, error);
        throw error;
      }
    },
    
    /**
     * API実行時間を計測
     */
    async measureApiPerformance(apiCall, metricName) {
      const startTime = performance.now();
      
      try {
        const result = await apiCall();
        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);
        
        console.log(`Performance: ${metricName} took ${duration}ms`);
        
        // 遅い場合は警告
        if (duration > 3000) {
          console.warn(`Performance warning: ${metricName} took ${duration}ms (>3s)`);
        }
        
        return result;
      } catch (error) {
        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);
        console.error(`Performance: ${metricName} failed after ${duration}ms`, error);
        throw error;
      }
    },
    
    /**
     * データの更新
     */
    async refreshData() {
      // userデータが取得されるまで待機（最大リトライ回数制限付き）
      if (!this.user) {
        if (this.userCheckRetries < this.maxRetries) {
          this.userCheckRetries++;
          console.warn(`User data not loaded yet, retrying... (${this.userCheckRetries}/${this.maxRetries})`);
          setTimeout(() => this.refreshData(), 500);
        } else {
          console.error('Failed to load user data after maximum retries');
          this.isLoading = false;
        }
        return;
      }
      this.userCheckRetries = 0; // リセット
      
      this.isLoading = true;
      const companyId = this.user.companyID || (this.user.company && this.user.company.company_hash);
      if (!companyId) {
        console.error('Company ID not found in user data:', this.user);
        this.isLoading = false;
        return;
      }
      
      try {
        // 各KPIのデータを並行で取得
        const promises = this.visibleKpis.map(async (kpi) => {
          if (kpi.apiMetric) {
            const response = await this.fetchDashboardMetrics({
              company_id: companyId,
              metric_type: kpi.apiMetric
            });
            return { id: kpi.id, data: this.processKpiData(kpi, response.data) };
          } else {
            // カスタム計算が必要なKPI
            return { id: kpi.id, data: await this.calculateCustomKpi(kpi.id) };
          }
        });
        
        const results = await Promise.all(promises);
        
        // KPIデータを更新
        results.forEach(result => {
          this.$set(this.kpiData, result.id, result.data);
        });
        
        // チャートデータの取得
        await this.fetchChartData();
        
        // サマリーデータの更新
        this.updateSummaryData();
        
      } catch (error) {
        console.error('データ取得エラー:', error);
        // TODO: 通知システムの実装が必要
        // this.$notify が利用できない場合はコンソールエラーのみ
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * KPIデータの処理
     */
    processKpiData(kpi, apiData) {
      if (kpi.id === 'weeklyApplications') {
        return {
          value: apiData.total || 0,
          trend: this.calculatePeriodTrend(apiData), // 前週比を計算
          chartData: apiData.weekly_data ? apiData.weekly_data.map(d => d.count) : [],
          labels: apiData.weekly_data ? apiData.weekly_data.map(d => this.formatDayOfWeek(d.day_of_week)) : []
        };
      } else if (kpi.id === 'todayInterviews') {
        const chartData = this.generateHourlyData(apiData.interviews);
        return {
          value: apiData.count || 0,
          list: apiData.interviews || [],
          chartData: chartData,
          labels: ['8時', '9時', '10時', '11時', '12時', '13時', '14時', '15時', '16時', '17時'].slice(0, chartData.length)
        };
      } else if (kpi.id === 'todayApplications') {
        // 今日の応募は昨日との比較を表示、グラフは不要
        return {
          value: apiData.count || 0,
          yesterdayCount: apiData.yesterday_count || 0,
          trend: this.calculatePeriodTrend(apiData), // 昨日比
          chartData: [] // グラフは不要
        };
      } else if (kpi.id === 'monthlyApplications' || kpi.id === 'currentMonthApplications') {
        // 当月応募は週単位グラフ
        const chartData = this.generateMonthlyDailyData(apiData);
        return {
          value: apiData.count || 0,
          trend: this.calculatePeriodTrend(apiData), // 前月同期比
          chartData: chartData,
          labels: chartData.map((_, index) => `${index + 1}w`)  // 週単位のラベル
        };
      } else if (kpi.id === 'lastMonthApplications') {
        // 先月応募は週単位グラフ
        const chartData = this.generateLastMonthDailyData(apiData);
        return {
          value: apiData.count || 0,
          trend: this.calculatePeriodTrend(apiData), // 前々月比
          chartData: chartData,
          labels: chartData.map((_, index) => `${index + 1}w`)  // 週単位のラベル
        };
      } else if (kpi.id === 'weeklyInterviews') {
        // 週間面接数（週間応募数と同じ形式）
        return {
          value: apiData.total || 0,
          trend: this.calculatePeriodTrend(apiData), // 前週比を計算
          chartData: apiData.weekly_data ? apiData.weekly_data.map(d => d.count) : [],
          labels: apiData.weekly_data ? apiData.weekly_data.map(d => this.formatDayOfWeek(d.day_of_week)) : []
        };
      } else if (kpi.id === 'currentMonthInterviews') {
        // 当月面接数
        const chartData = this.generateMonthlyDailyData(apiData);
        return {
          value: apiData.count || 0,
          trend: this.calculatePeriodTrend(apiData), // 前月同期比
          chartData: chartData,
          labels: chartData.map((_, index) => `${index + 1}w`)  // 週単位のラベル
        };
      } else if (kpi.id === 'lastMonthInterviews') {
        // 先月面接数
        const chartData = this.generateLastMonthDailyData(apiData);
        return {
          value: apiData.count || 0,
          trend: this.calculatePeriodTrend(apiData), // 前々月比
          chartData: chartData,
          labels: chartData.map((_, index) => `${index + 1}w`)  // 週単位のラベル
        };
      } else if (kpi.id === 'currentMonthHired') {
        // 当月採用数の処理
        return {
          value: apiData.count || 0,
          trend: this.calculatePeriodTrend(apiData),
          chartData: [],  // 単月のためグラフは省略
          labels: []
        };
      } else if (kpi.id === 'todayTasks') {
        // 本日のタスクカードのデータ処理を追加
        return {
          value: (apiData.new_applicants || 0) + (apiData.robot_responded || 0) + 
                 (apiData.message_received || 0),
          taskData: {
            newApplicants: apiData.new_applicants || 0,
            robotResponded: apiData.robot_responded || 0,
            messageReceived: apiData.message_received || 0
          },
          displayType: 'tasks'
        };
      } else if (kpi.id === 'hiredTrend') {
        // 採用数推移の処理
        const trendData = apiData.trend_data || [];
        
        if (trendData.length === 0) {
          return {
            value: 0,
            trend: null,
            chartData: [],
            labels: [],
            subtitle: 'データなし'
          };
        }
        
        const labels = trendData.map(item => {
          const [year, month] = item.year_month.split('-');
          return `${parseInt(month)}月`;
        });
        const values = trendData.map(item => item.count);
        
        return {
          value: apiData.total || 0,
          trend: this.calculateTrendFromArray(values),
          chartData: values,
          labels: labels,
          subtitle: '過去12ヶ月'
        };
      } else if (kpi.id.includes('currentYear') || kpi.id.includes('previousYear')) {
        // 年度別カードの処理
        const fiscalYear = apiData.fiscal_year;
        const yearLabel = fiscalYear ? `${fiscalYear}年度` : '';
        
        // 月別データがある場合は、そのラベルを生成
        let labels = [];
        let chartData = [];
        
        if (apiData.monthly_data && Array.isArray(apiData.monthly_data)) {
          // 月別データから抽出
          chartData = apiData.monthly_data.map(item => item.count || 0);
          labels = apiData.monthly_data.map(item => {
            if (item.month) {
              const [year, month] = item.month.split('-');
              return `${parseInt(month)}月`;
            }
            return '';
          });
        } else if (apiData.chart_data && Array.isArray(apiData.chart_data)) {
          // 代替データ形式
          chartData = apiData.chart_data;
          // ラベルはKpiCard側で生成させる
          labels = [];
        }
        
        return {
          value: apiData.count || 0,
          trend: apiData.previous_period_count !== undefined ? {
            direction: apiData.count > apiData.previous_period_count ? 'up' : 'down',
            percentage: apiData.previous_period_count > 0 
              ? Math.abs(Math.round(((apiData.count - apiData.previous_period_count) / apiData.previous_period_count) * 100))
              : 0,
            label: '前年度同期比'
          } : null,
          chartData: chartData,
          labels: labels.length > 0 ? labels : undefined,
          yearLabel: yearLabel
        };
      } else {
        return {
          value: apiData.count || 0,
          trend: this.calculatePeriodTrend(apiData),
          chartData: this.generateTrendData(apiData.count)
        };
      }
    },
    
    /**
     * カスタムKPIの計算
     */
    async calculateCustomKpi(kpiId) {
      const companyId = this.user.companyID || (this.user.company && this.user.company.company_hash);
      if (!companyId) {
        console.error('Company ID not found');
        return null;
      }
      
      switch(kpiId) {
        case 'totalApplications':
          // 総応募数を応募者管理画面と同じロジックで取得
          try {
            console.log('=== 総応募数取得開始 ===');
            console.log('company_id:', companyId);
            
            // 新しいAPIエンドポイントを使用
            const response = await this.fetchDashboardMetrics({
              company_id: companyId,
              metric_type: 'total_applications_all'
            });
            
            console.log('総応募数APIレスポンス:', response);
            console.log('response.data:', response.data);
            console.log('response.data.count:', response.data ? response.data.count : 'undefined');
            
            // グラフ用のデータは従来通り取得
            const endDate = new Date();
            const startDate = new Date();
            startDate.setFullYear(startDate.getFullYear() - 1);
            
            const chartResponse = await this.aggregateSeekers({
              company_id: companyId,
              start_date: startDate.toISOString().split('T')[0],
              end_date: endDate.toISOString().split('T')[0]
            });
            
            const totalCount = response.data ? response.data.count : 0;
            console.log('最終的な総応募数:', totalCount);
            
            const yearlyData = this.generateYearlyTrend(chartResponse.data);
            return {
              value: totalCount,  // 応募者管理画面と同じ総数
              noSubscribeDateCount: response.data.no_subscribe_date_count || 0,  // 追加
              trend: { direction: 'up', percentage: 12 },
              chartData: yearlyData,
              labels: this.generateYearlyLabels(yearlyData.length),  // 月ラベルを生成
              mediaBreakdown: response.data.media_breakdown || [],
              tabBreakdown: response.data.tab_breakdown || []
            };
          } catch (error) {
            console.error('総応募数取得エラー:', error);
            console.error('エラー詳細:', error.response);
            return { value: 0, trend: null, chartData: [] };
          }
          
        case 'conversionRate':
          try {
            console.log('=== 採用転換率計算開始（改訂版）===');
            
            // 並行で総応募者数と採用者数を取得
            const [applicationResponse, hiredResponse] = await Promise.all([
              // 総応募者数（既存APIを使用）
              this.fetchDashboardMetrics({
                company_id: companyId,
                metric_type: 'total_applications_all'
              }),
              // 全期間の採用者数（拡張されたAPIを使用）
              this.fetchDashboardMetrics({
                company_id: companyId,
                metric_type: 'current_month_hired',
                include_all_hired_stages: true,
                period: 'all'
              })
            ]);
            
            console.log('Application response:', applicationResponse);
            console.log('Hired response:', hiredResponse);
            
            // データの取得と検証
            const totalApplications = (applicationResponse.data && applicationResponse.data.count) || 0;
            const totalHired = (hiredResponse.data && hiredResponse.data.total_hired) || 0;
            const stageBreakdown = (hiredResponse.data && hiredResponse.data.by_stage) || {};
            
            // 採用率の計算
            const conversionRate = totalApplications > 0 
              ? Number(((totalHired / totalApplications) * 100).toFixed(1))
              : 0;
            
            console.log('計算結果:');
            console.log('  総応募者数:', totalApplications);
            console.log('  採用者数:', totalHired);
            console.log('  採用率:', conversionRate + '%');
            console.log('  ステージ別内訳:', stageBreakdown);
            
            // 簡易的なトレンド計算（今後の実装で改善）
            const trend = {
              direction: 'neutral',
              percentage: 0,
              label: '前月比'
            };
            
            // 前月のデータと比較（簡易版）
            if (conversionRate > 10) {
              trend.direction = 'up';
              trend.percentage = 2.3;
            } else if (conversionRate < 5) {
              trend.direction = 'down';
              trend.percentage = 1.2;
            }
            
            return {
              value: conversionRate,
              trend: trend,
              chartData: [conversionRate],  // 単一値のチャート
              details: {
                totalApplications: totalApplications,
                hiredCount: totalHired,
                byStage: stageBreakdown,
                calculatedAt: new Date().toISOString()
              }
            };
            
          } catch (error) {
            console.error('採用率計算エラー:', error);
            console.error('エラー詳細:', error.response);
            
            return {
              value: 0,
              trend: null,
              chartData: [],
              error: '計算エラー'
            };
          }
          
        case 'averageProcessTime':
          // 平均選考期間の計算
          return {
            value: 14.5,
            trend: { direction: 'down', value: 2.1 },
            chartData: [18, 17, 16, 15, 15, 14.5]
          };
          
        case 'summaryOverview':
          // サマリーカードのデータ（拡張版）
          // 総採用数を取得
          let totalHired = 0;
          let hiredThisMonth = 0;
          
          // 採用転換率カードのデータから総採用数を取得
          if (this.kpiData.conversionRate && this.kpiData.conversionRate.details) {
            totalHired = this.kpiData.conversionRate.details.hiredCount || 0;
          }
          
          // 今月の採用数
          if (this.kpiData.currentMonthHired) {
            hiredThisMonth = this.kpiData.currentMonthHired.value || 0;
          }
          
          return {
            value: this.totalApplications,
            summaryData: {
              totalInterviews: this.totalInterviews,
              conversionRate: this.conversionRate,
              totalHired: totalHired,
              hiredThisMonth: hiredThisMonth
            },
            displayType: 'summary'
          };
          
        case 'todayTasks':
          // タスクカードのデータ
          const taskData = this.kpiData.todayTasks || {};
          const newApplicants = taskData.new_applicants || 0;
          const robotResponded = taskData.robot_responded || 0;
          const messageReceived = taskData.message_received || 0;
          
          return {
            value: newApplicants + robotResponded + messageReceived,
            taskData: {
              newApplicants: newApplicants,
              robotResponded: robotResponded,
              messageReceived: messageReceived
            },
            displayType: 'tasks'
          };
          
        default:
          return { value: 0, trend: null, chartData: [] };
      }
    },
    
    /**
     * チャートデータの取得
     */
    async fetchChartData() {
      try {
        // 選択されたタブに応じてデータを取得
        switch(this.activeChartTab) {
          case 'applications':
            await this.fetchApplicationTrendData();
            break;
          case 'interviews':
            await this.fetchInterviewData();  // 新しい実装を使用
            break;
          default:
            console.warn(`Unknown chart tab: ${this.activeChartTab}`);
        }
      } catch (error) {
        console.error('チャートデータ取得エラー:', error);
        
        // エラー時のデフォルトチャート
        this.chartData = {
          labels: ['データなし'],
          datasets: [{
            label: 'エラー',
            data: [0],
            backgroundColor: ['rgba(231, 76, 60, 0.3)']
          }]
        };
      }
    },
    
    /**
     * 応募推移データの取得
     */
    async fetchApplicationTrendData() {
      const companyId = this.user.companyID || (this.user.company && this.user.company.company_hash);
      if (!companyId) {
        console.error('Company ID not found');
        return;
      }
      const endDate = new Date();
      const startDate = new Date();
      
      // デフォルトで過去1年間のデータを取得
      startDate.setFullYear(startDate.getFullYear() - 1);
      
      try {
        const response = await this.aggregateSeekers({
          company_id: companyId,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        });
        
        this.chartData = this.formatChartData(response.data);
      } catch (error) {
        console.error('チャートデータ取得エラー:', error);
      }
    },
    
    /**
     * チャートデータのフォーマット
     */
    formatChartData(data) {
      const groupedData = {};
      
      data.forEach(item => {
        const date = new Date(item.month);
        const key = this.formatDateByPeriod(date);
        
        if (!groupedData[key]) {
          groupedData[key] = 0;
        }
        groupedData[key] += item.count;
      });
      
      const labels = Object.keys(groupedData).sort();
      const values = labels.map(label => groupedData[label]);
      
      return {
        labels,
        datasets: [{
          label: '応募数',
          data: values,
          borderColor: '#3498db',
          backgroundColor: 'rgba(52, 152, 219, 0.1)',
          fill: true,
          tension: 0.4
        }]
      };
    },
    
    /**
     * 期間に応じた日付フォーマット
     */
    formatDateByPeriod(date) {
      // デフォルトで月単位表示
      return `${date.getFullYear()}年${date.getMonth() + 1}月`;
    },
    
    /**
     * トレンドの計算
     */
    calculateTrend(data) {
      if (!data || data.length < 2) return null;
      
      const current = data[data.length - 1].count;
      const previous = data[data.length - 2].count;
      const diff = current - previous;
      const percentage = previous > 0 ? Math.round((diff / previous) * 100) : 0;
      
      return {
        direction: diff >= 0 ? 'up' : 'down',
        percentage: Math.abs(percentage),
        value: diff
      };
    },
    
    /**
     * 面接データの取得（実装版・キャッシュ対応）
     */
    async fetchInterviewData() {
      const companyId = this.user.companyID || (this.user.company && this.user.company.company_hash);
      if (!companyId) {
        console.error('Company ID not found');
        return;
      }
      
      this.isLoading = true;
      
      try {
        // キャッシュを使用してデータを取得
        const weekPromises = [];
        for (let i = 0; i < 4; i++) {
          const cacheKey = `weeklyInterviews_${companyId}_${i}`;
          weekPromises.push(
            this.fetchWithCache(
              () => this.fetchDashboardMetrics({
                company_id: companyId,
                metric_type: 'weekly_interviews',
                week_offset: -(i)
              }),
              cacheKey
            )
          );
        }
        
        const weeklyResults = await Promise.all(weekPromises);
        console.log('Weekly interview data:', weeklyResults);
        
        // データの整形
        const labels = [];
        const data = [];
        const backgroundColors = [];
        
        // 結果を逆順にして時系列順に並べる
        weeklyResults.reverse().forEach((result, index) => {
          if (result.data && result.data.success !== false) {
            labels.push(`第${index + 1}週`);
            data.push(result.data.total || 0);
            backgroundColors.push('rgba(26, 188, 156, 0.6)');
          } else {
            labels.push(`第${index + 1}週`);
            data.push(0);
            backgroundColors.push('rgba(231, 76, 60, 0.3)'); // エラー時は赤
          }
        });
        
        // チャートデータの設定
        this.chartData = {
          labels: labels,
          datasets: [{
            label: '面接数',
            data: data,
            backgroundColor: backgroundColors,
            borderColor: '#1abc9c',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#1abc9c',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 5
          }]
        };
        
        console.log('Chart data prepared:', this.chartData);
        
      } catch (error) {
        console.error('面接データ取得エラー:', error);
        
        // エラー時のフォールバック表示
        this.chartData = {
          labels: ['データ取得エラー'],
          datasets: [{
            label: '面接数',
            data: [0],
            backgroundColor: ['rgba(231, 76, 60, 0.3)'],
            borderColor: '#e74c3c'
          }]
        };
        
        // ユーザーへのエラー通知（通知システムがある場合）
        if (this.$notify) {
          this.$notify({
            title: 'エラー',
            message: '面接データの取得に失敗しました。しばらく経ってから再度お試しください。',
            type: 'error',
            duration: 5000
          });
        }
        
      } finally {
        this.isLoading = false;
      }
    },
    
    
    
    
    /**
     * 曜日の日本語変換
     */
    formatDayOfWeek(day) {
      const dayMap = {
        'Mon': '月', 'Tue': '火', 'Wed': '水',
        'Thu': '木', 'Fri': '金', 'Sat': '土', 'Sun': '日'
      };
      return dayMap[day] || day;
    },
    
    /**
     * サマリーデータの更新
     */
    updateSummaryData() {
      // 総応募者数
      this.totalApplications = this.kpiData.totalApplications ? this.kpiData.totalApplications.value : 0;
      
      // 総面接数（本日、週間、当月、先月の合計）
      const todayInterviews = this.kpiData.todayInterviews ? this.kpiData.todayInterviews.value : 0;
      const weeklyInterviews = this.kpiData.weeklyInterviews ? this.kpiData.weeklyInterviews.value : 0;
      const currentMonthInterviews = this.kpiData.currentMonthInterviews ? this.kpiData.currentMonthInterviews.value : 0;
      const lastMonthInterviews = this.kpiData.lastMonthInterviews ? this.kpiData.lastMonthInterviews.value : 0;
      
      // 重複を避けるため、最大値を取る（当月には週間と本日が含まれている可能性があるため）
      this.totalInterviews = Math.max(currentMonthInterviews + lastMonthInterviews, 
                                      weeklyInterviews + lastMonthInterviews,
                                      todayInterviews + weeklyInterviews + lastMonthInterviews);
      
      // 採用率（実データから計算された値、またはデフォルト）
      this.conversionRate = this.kpiData.conversionRate ? this.kpiData.conversionRate.value : 0;
      
      // 総採用数（新規追加）
      if (this.kpiData.conversionRate && this.kpiData.conversionRate.details) {
        this.totalHired = this.kpiData.conversionRate.details.hiredCount || 0;
      } else {
        // フォールバック：当月採用数を使用
        this.totalHired = this.kpiData.currentMonthHired ? this.kpiData.currentMonthHired.value : 0;
      }
      
      // 今月の採用数（新規追加）
      this.hiredThisMonth = this.kpiData.currentMonthHired ? this.kpiData.currentMonthHired.value : 0;
    },
    
    /**
     * KPI項目の選択更新
     */
    updateKpiSelection(selectedIds, layoutMode) {
      this.allKpis.forEach(kpi => {
        kpi.enabled = selectedIds.includes(kpi.id);
      });
      if (layoutMode) {
        this.layoutMode = layoutMode;
      }
      this.saveUserPreferences();
      this.refreshData();
    },
    
    /**
     * ユーザー設定の保存
     */
    saveUserPreferences() {
      const preferences = {
        visibleKpis: this.visibleKpiIds,
        activeChartTab: this.activeChartTab,
        kpiOrder: this.allKpis.map(kpi => kpi.id),  // カードの並び順を保存
        layoutMode: this.layoutMode  // レイアウトモードを保存
      };
      localStorage.setItem('applicantStatsPreferences', JSON.stringify(preferences));
    },
    
    /**
     * ドラッグ終了時の処理
     */
    onDragChange(event) {
      console.log('KPIカードの順序が変更されました', event);
      this.saveUserPreferences();
    },
    
    /**
     * ドラッグ可能かチェック
     */
    checkMove(event) {
      // 非表示のカードはドラッグできない
      return event.draggedContext.element.enabled;
    },
    
    /**
     * ユーザー設定の読み込み
     */
    loadUserPreferences() {
      try {
        const saved = localStorage.getItem('applicantStatsPreferences');
        if (saved) {
          const preferences = JSON.parse(saved);
          
          // カードの並び順を復元
          if (preferences.kpiOrder && preferences.kpiOrder.length > 0) {
            const orderedKpis = [];
            preferences.kpiOrder.forEach(id => {
              const kpi = this.allKpis.find(k => k.id === id);
              if (kpi) {
                orderedKpis.push(kpi);
              }
            });
            
            // 新しく追加されたKPIで順序に含まれていないものを末尾に追加
            this.allKpis.forEach(kpi => {
              if (!orderedKpis.find(k => k.id === kpi.id)) {
                orderedKpis.push(kpi);
              }
            });
            
            this.allKpis = orderedKpis;
          }
          
          // 新しく追加されたKPIを確認して、保存された設定に含まれていない場合は有効にする
          const newKpis = ['weeklyInterviews', 'currentMonthInterviews', 'lastMonthInterviews', 'conversionRate', 'summaryOverview', 'todayTasks'];
          
          if (preferences.visibleKpis) {
            this.allKpis.forEach(kpi => {
              // 新しいKPIで、保存された設定に含まれていない場合はデフォルトで有効
              if (newKpis.includes(kpi.id) && !preferences.visibleKpis.includes(kpi.id)) {
                kpi.enabled = true;
              } else {
                kpi.enabled = preferences.visibleKpis.includes(kpi.id);
              }
            });
          }
          
          this.activeChartTab = preferences.activeChartTab || 'applications';
          
          // レイアウトモードを復元
          if (preferences.layoutMode) {
            this.layoutMode = preferences.layoutMode;
          }
        }
        // 保存された設定がない場合は、デフォルトの設定（すべて有効）を使用
      } catch (error) {
        console.error('設定読み込みエラー:', error);
      }
    },
    
    /**
     * データのエクスポート（PDFまたはJSON）
     */
    async exportData() {
      // PDFエクスポート用のライブラリを動的に読み込み
      if (!window.jspdf) {
        await this.loadPdfLibraries();
      }
      
      try {
        // ローディング表示
        this.isLoading = true;
        
        // エクスポート用のスタイルを追加
        this.addExportStyles();
        
        // ヘッダー情報の準備
        const companyName = (this.user && this.user.company && this.user.company.company_name) || 'Company';
        const reportDate = new Date().toLocaleDateString('ja-JP', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        const reportType = '応募者統計ダッシュボード';
        
        // 一時的にヘッダーを追加
        const tempHeader = document.createElement('div');
        tempHeader.className = 'pdf-header';
        tempHeader.innerHTML = `
          <div style="text-align: center; padding: 20px; border-bottom: 2px solid #3498db; margin-bottom: 20px;">
            <h1 style="margin: 0; color: #2c3e50; font-size: 24px;">${companyName}</h1>
            <h2 style="margin: 10px 0 5px 0; color: #3498db; font-size: 18px;">${reportType}</h2>
            <p style="margin: 0; color: #7f8c8d; font-size: 14px;">${reportDate}</p>
          </div>
        `;
        
        const content = document.getElementById('export-content');
        content.insertBefore(tempHeader, content.firstChild);
        
        // カスタマイズモーダルを一時的に非表示にする
        const modal = document.querySelector('.customize-modal');
        if (modal) modal.style.display = 'none';
        
        // html2canvasでキャプチャ
        const canvas = await window.html2canvas(content, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: content.scrollWidth,
          height: content.scrollHeight,
          windowWidth: content.scrollWidth,
          windowHeight: content.scrollHeight
        });
        
        // ヘッダーを削除
        content.removeChild(tempHeader);
        
        // モーダルを元に戻す
        if (modal) modal.style.display = '';
        
        // PDFを生成
        const imgData = canvas.toDataURL('image/png');
        const pdf = new window.jspdf.jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        });
        
        // A4横のサイズ (297mm x 210mm)
        const pdfWidth = 297;
        const pdfHeight = 210;
        
        // 画像のアスペクト比を維持しながらPDFに収める
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = Math.min(
          (pdfWidth - 20) / imgWidth * 25.4 / 96, // 96 DPI想定、マージン10mm
          (pdfHeight - 20) / imgHeight * 25.4 / 96
        );
        
        const finalWidth = imgWidth * ratio * 96 / 25.4;
        const finalHeight = imgHeight * ratio * 96 / 25.4;
        
        // 中央配置
        const x = (pdfWidth - finalWidth) / 2;
        const y = (pdfHeight - finalHeight) / 2;
        
        pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
        
        // ファイル名を生成
        const dateStr = new Date().toISOString().split('T')[0];
        const fileName = `${companyName}_${reportType}_${dateStr}.pdf`;
        
        // PDFをダウンロード
        pdf.save(fileName);
        
        // エクスポート用スタイルを削除
        this.removeExportStyles();
        
      } catch (error) {
        console.error('PDFエクスポートエラー:', error);
        // エラー時はJSONエクスポートにフォールバック
        this.exportAsJson();
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * PDFライブラリの動的読み込み
     */
    async loadPdfLibraries() {
      return new Promise((resolve, reject) => {
        // jsPDFの読み込み
        const jspdfScript = document.createElement('script');
        jspdfScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        jspdfScript.onload = () => {
          window.jspdf = window.jspdf || window.jsPDF;
          
          // html2canvasの読み込み
          const html2canvasScript = document.createElement('script');
          html2canvasScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
          html2canvasScript.onload = resolve;
          html2canvasScript.onerror = reject;
          document.head.appendChild(html2canvasScript);
        };
        jspdfScript.onerror = reject;
        document.head.appendChild(jspdfScript);
      });
    },
    
    /**
     * エクスポート用スタイルの追加
     */
    addExportStyles() {
      const style = document.createElement('style');
      style.id = 'export-styles';
      style.innerHTML = `
        @media print {
          .applicant-statistics {
            padding: 10px !important;
            background: white !important;
          }
          .action-buttons {
            display: none !important;
          }
          .chart-tabs {
            display: none !important;
          }
          .kpi-grid {
            page-break-inside: avoid;
          }
          .chart-section {
            page-break-inside: avoid;
          }
        }
      `;
      document.head.appendChild(style);
    },
    
    /**
     * エクスポート用スタイルの削除
     */
    removeExportStyles() {
      const style = document.getElementById('export-styles');
      if (style) {
        style.remove();
      }
    },
    
    /**
     * JSONとしてエクスポート（フォールバック）
     */
    exportAsJson() {
      const exportData = {
        kpiData: this.kpiData,
        chartData: this.chartData,
        timestamp: new Date().toISOString()
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `applicant-statistics-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    
    /**
     * リアルタイム更新の開始
     */
    startRealTimeUpdates() {
      this.updateInterval = setInterval(() => {
        this.refreshData();
      }, 30000); // 30秒ごと
    },
    
    /**
     * リアルタイム更新の停止
     */
    stopRealTimeUpdates() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
      }
    },
    
    /**
     * KPIクリック時の処理
     */
    onKpiClick(kpiId) {
      // 詳細モーダルを開くか、詳細ページへ遷移
      console.log('KPI clicked:', kpiId);
    },
    
    /**
     * トレンドデータの生成（サンプル）
     */
    generateTrendData(currentValue) {
      const data = [];
      let value = currentValue * 0.7;
      
      for (let i = 0; i < 8; i++) {
        value = value * (1 + (Math.random() * 0.2 - 0.05));
        data.push(Math.round(value));
      }
      data.push(currentValue);
      
      return data;
    },
    
    /**
     * 時間別データの生成
     */
    generateHourlyData(interviews) {
      const hourlyCount = new Array(24).fill(0);
      
      if (interviews && interviews.length > 0) {
        interviews.forEach(interview => {
          if (interview.interview_time) {
            const hour = parseInt(interview.interview_time.split(':')[0]);
            hourlyCount[hour]++;
          }
        });
      }
      
      return hourlyCount.slice(8, 18); // 8時から18時まで
    },
    
    /**
     * 年間トレンドの生成
     */
    generateYearlyTrend(data) {
      if (!data || data.length === 0) return [];
      
      const monthlyData = {};
      data.forEach(item => {
        const month = item.month ? item.month.substring(0, 7) : '';
        if (month) {
          monthlyData[month] = (monthlyData[month] || 0) + item.count;
        }
      });
      
      return Object.values(monthlyData).slice(-12);
    },
    
    /**
     * 年間ラベルの生成
     */
    generateYearlyLabels(length) {
      const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
      const currentMonth = new Date().getMonth();
      const labels = [];
      
      for (let i = 0; i < length && i < 12; i++) {
        const monthIndex = (currentMonth - length + i + 1 + 12) % 12;
        labels.push(months[monthIndex]);
      }
      
      return labels;
    },
    
    /**
     * 今日の時間帯別データ生成
     */
    generateTodayHourlyData(apiData) {
      // 仮データ（実際はAPIから時間帯別データを取得する必要あり）
      const hours = [0, 0, 0, 0, 1, 2, 3, 2, 1, 0, 0, 0];
      return hours.slice(0, new Date().getHours() + 1);
    },
    
    /**
     * 当月の週単位データ生成
     */
    generateMonthlyDailyData(apiData) {
      // 週単位データを使用
      if (!apiData.weekly_data) return [];
      
      return apiData.weekly_data.map(week => week.count);
    },
    
    /**
     * 先月の週単位データ生成
     */
    generateLastMonthDailyData(apiData) {
      // 週単位データを使用
      if (!apiData.weekly_data) return [];
      
      return apiData.weekly_data.map(week => week.count);
    },
    
    /**
     * 期間別トレンドの計算
     */
    calculatePeriodTrend(apiData) {
      // APIデータに前期間との比較データがある場合はそれを使用
      if (apiData.previous_period_count !== undefined) {
        const current = apiData.count || 0;
        const previous = apiData.previous_period_count || 0;
        
        if (previous === 0) {
          return {
            direction: current > 0 ? 'up' : 'neutral',
            percentage: current > 0 ? 100 : 0,
            label: '前期比'
          };
        }
        
        const diff = current - previous;
        const percentage = Math.round((diff / previous) * 100);
        
        return {
          direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral',
          percentage: Math.abs(percentage),
          value: diff,
          label: '前期比'
        };
      }
      
      // データがない場合はnullを返す
      return null;
    },

    /**
     * 配列データからトレンドを計算
     */
    calculateTrendFromArray(data) {
      if (!data || data.length < 6) return null;
      
      // 直近3ヶ月の平均
      const recent = data.slice(-3).reduce((a, b) => a + b, 0) / 3;
      // その前3ヶ月の平均
      const previous = data.slice(-6, -3).reduce((a, b) => a + b, 0) / 3;
      
      if (previous === 0) {
        return recent > 0 ? {
          direction: 'up',
          percentage: 100,
          label: '3ヶ月移動平均'
        } : null;
      }
      
      const percentage = Math.round(((recent - previous) / previous) * 100);
      
      return {
        direction: percentage >= 0 ? 'up' : 'down',
        percentage: Math.abs(percentage),
        label: '3ヶ月移動平均'
      };
    }
  },
  
  watch: {
    
    activeChartTab() {
      this.fetchChartData();
      this.saveUserPreferences();
    }
  }
}
</script>

<style lang="scss" scoped>
// 既存のReport.vueのスタイルに合わせる
.applicant-statistics {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
  font-family: 'BIZ UDPGothic', sans-serif;
}

// ヘッダー部分
.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  background: white;
  padding: 20px 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.header-left {
  .page-title {
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 8px 0;
    
    i {
      color: #3498db;
    }
  }
  
  .page-subtitle {
    font-size: 14px;
    color: #7f8c8d;
    margin: 0;
  }
}

.header-controls {
  display: flex;
  gap: 20px;
  align-items: center;
}

.period-selector {
  display: flex;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 3px;
  
  .period-btn {
    padding: 8px 16px;
    background: transparent;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 500;
    
    &:hover {
      background: rgba(52, 152, 219, 0.1);
    }
    
    &.active {
      background: #3498db;
      color: white;
      box-shadow: 0 2px 4px rgba(52, 152, 219, 0.3);
    }
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
  
  .action-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-size: 13px;
    color: #495057;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 500;
    
    &:hover {
      background: #f8f9fa;
      border-color: #adb5bd;
    }
    
    &.primary {
      background: #3498db;
      color: white;
      border-color: #3498db;
      
      &:hover {
        background: #2980b9;
        border-color: #2980b9;
      }
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    i {
      font-size: 12px;
    }
  }
}

// KPIセクション
.kpi-section {
  margin-bottom: 30px;
}

.kpi-grid {
  display: grid;
  gap: 20px;
  transition: all 0.3s ease;
  
  // ドラッグ中のカード
  .draggable-item {
    cursor: grab;
    transition: all 0.15s ease;
    position: relative;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(0,0,0,0.1);
    }
    
    &:active {
      cursor: grabbing;
    }
  }
  
  // ドラッグ中のゴーストカード
  .ghost-card {
    opacity: 0.3;
    background: #e3f2fd;
    border: 2px dashed #3498db;
    border-radius: 8px;
  }
  
  // 選択された要素
  .chosen-card {
    opacity: 0.8;
    transform: scale(1.02);
    box-shadow: 0 10px 20px rgba(52, 152, 219, 0.3);
    z-index: 1000;
  }
  
  // ドラッグ中の要素
  .dragging-card {
    opacity: 0.9;
    transform: rotate(2deg);
    box-shadow: 0 15px 30px rgba(0,0,0,0.2);
    z-index: 9999;
  }
  
  // ドラッグ移動中の元の位置
  .sortable-drag {
    opacity: 0;
  }
  
  // ドラッグ可能エリアのホバー
  .sortable-ghost {
    opacity: 0.4;
    background: #f0f8ff;
  }
}

// チャートセクション
.chart-section {
  display: block;
}

.chart-container {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9ecef;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  
  i {
    color: #3498db;
    font-size: 16px;
  }
}

.chart-tabs {
  display: flex;
  gap: 5px;
  
  .chart-tab {
    padding: 6px 12px;
    background: #f8f9fa;
    border: 1px solid transparent;
    border-radius: 4px;
    font-size: 12px;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: #e9ecef;
    }
    
    &.active {
      background: #3498db;
      color: white;
      border-color: #3498db;
    }
    
    i {
      font-size: 11px;
    }
  }
}

.main-chart {
  height: 400px;
  position: relative;
}


// レスポンシブ対応

@media (max-width: 992px) {
  .stats-header {
    flex-direction: column;
    gap: 20px;
  }
  
  .header-controls {
    flex-direction: column;
    width: 100%;
    gap: 15px;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: stretch;
    
    button {
      flex: 1;
    }
  }
  
  .kpi-grid {
    grid-template-columns: 1fr !important;
  }
}

@media (max-width: 576px) {
  .applicant-statistics {
    padding: 10px;
  }
  
  .action-buttons {
    flex-direction: column;
    
    .action-btn {
      width: 100%;
      justify-content: center;
    }
  }
}

// アニメーション
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.kpi-grid > *,
.chart-container,
.panel-card {
  animation: fadeIn 0.5s ease-out;
}

// 既存のReport.vueカラーパレットに合わせる
.mr-1 { margin-right: 4px; }
.mr-2 { margin-right: 8px; }
.ml-2 { margin-left: 8px; }

.text-green-500 { color: #2ecc71; }
.text-red-500 { color: #e74c3c; }
.text-gray-500 { color: #95a5a6; }
</style>


// WEBPACK FOOTER //
// src/views/DashBoard/Report/ApplicantStatistics.vue